import { Filter } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  pathwayLabel,
  TASK_QUEUE_PRIORITY_GROUPS,
  TASK_QUEUE_REASON_LABELS,
} from '../../constants';
import { formatPatientMrn } from '../../helpers';
import { Badge } from '../Badge';
import styles from './PatientTaskSearch.module.scss';

const SEARCH_DEBOUNCE_MS = 400;

const PatientTaskSearch = ({
  patientSearch,
  setPatientSearch,
  getPatientSearchLabel,
  getPatientSearchPathway,
  patientSearchMetadataById,
  searchOptions,
  searchPlaceholder,
  isSearchLoading,
  onExpandSearchScope,
  onSearchPatients,
  onClearSearch,
}) => {
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isPatientSearchOpen, setIsPatientSearchOpen] = useState(false);
  const patientSearchRef = useRef(null);

  const clearPatientSearch = async () => {
    setPatientSearchQuery('');
    setPatientSearchResults([]);
    setIsPatientSearchOpen(false);

    if (onClearSearch) {
      await onClearSearch();
      return;
    }

    setPatientSearch(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!patientSearchRef.current?.contains(event.target)) {
        if (!patientSearch && patientSearchQuery.trim()) {
          clearPatientSearch().catch(() => null);
          return;
        }

        setIsPatientSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [patientSearch, patientSearchQuery]);

  const normalizedSearchOptions = useMemo(() => searchOptions ?? [], [searchOptions]);

  const getSearchText = (searchOption) => {
    const value = searchOption?.value ?? {};

    if (value.kind === 'reason') {
      return `${searchOption?.label || ''} ${TASK_QUEUE_REASON_LABELS[Number(value.reasonId)] || ''}`
        .trim()
        .toLowerCase();
    }

    if (value.kind === 'priority') {
      return `${searchOption?.label || ''}`.trim().toLowerCase();
    }

    return [
      searchOption?.label || '',
      value?.name || '',
      value?.preferredName || '',
      value?.firstName || '',
      value?.lastName || '',
      value?.mrn || '',
      value?.id || '',
    ]
      .join(' ')
      .trim()
      .toLowerCase();
  };

  useEffect(() => {
    const trimmedQuery = patientSearchQuery.trim().toLowerCase();

    if (patientSearch || trimmedQuery.length < 1) {
      setPatientSearchResults([]);
      setIsPatientSearchOpen(false);
      return undefined;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      if (onSearchPatients) {
        try {
          const results = await onSearchPatients(trimmedQuery);
          if (isCancelled) return;

          setPatientSearchResults(results || []);
          setIsPatientSearchOpen(true);
          return;
        } catch (error) {
          if (isCancelled) return;
          setPatientSearchResults([]);
          setIsPatientSearchOpen(true);
          return;
        }
      }

      await onExpandSearchScope?.();
      if (isCancelled) return;

      const results = normalizedSearchOptions.filter((option) =>
        getSearchText(option).includes(trimmedQuery),
      );

      setPatientSearchResults(results);
      setIsPatientSearchOpen(true);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    normalizedSearchOptions,
    onExpandSearchScope,
    onSearchPatients,
    patientSearch,
    patientSearchQuery,
  ]);

  const renderPatientSearchOption = (searchOption) => {
    const patient = searchOption?.value;

    if (patient?.kind === 'reason') {
      return (
        <div className={styles.patientSearchOption}>
          <div className={styles.patientSearchOptionHeader}>
            <span className={styles.patientSearchOptionName}>
              {TASK_QUEUE_REASON_LABELS[Number(patient.reasonId)] || searchOption.label}
            </span>
            <span className={styles.patientSearchOptionCount}>{searchOption?.count ?? 0}</span>
          </div>
        </div>
      );
    }

    if (patient?.kind === 'priority') {
      const priorityGroup =
        TASK_QUEUE_PRIORITY_GROUPS.find((group) => group.key === patient.priority) || null;

      return (
        <div className={styles.patientSearchOption}>
          <div className={styles.patientSearchOptionHeader}>
            <Badge variant={priorityGroup?.badgeVariant || 'outline'}>
              {priorityGroup?.label || searchOption.label}
            </Badge>
            <span className={styles.patientSearchOptionCount}>{searchOption?.count ?? 0}</span>
          </div>
        </div>
      );
    }

    const metadata = patientSearchMetadataById[`${patient?.id ?? ''}`] ?? {};
    const fallbackName = `${patient?.name || ''}`.trim();
    const firstName = patient?.preferredName || patient?.firstName || '';
    const lastName = patient?.lastName || '';
    const patientName = `${firstName} ${lastName}`.trim() || fallbackName || 'Unknown Patient';
    const pathway = metadata.pathway || getPatientSearchPathway(patient);
    const pathwayDisplay = pathwayLabel[pathway] ?? pathway;
    const phase = patient?.phase ?? metadata.phase ?? 1;
    const clinician =
      patient?.clinician ||
      patient?.providerName ||
      patient?.doctorName ||
      metadata.clinician ||
      null;

    return (
      <div className={styles.patientSearchOption}>
        <div className={styles.patientSearchOptionHeader}>
          <span className={styles.patientSearchOptionName}>{patientName}</span>
          <span className={styles.patientSearchOptionMrn}>
            {metadata.patientMrn || formatPatientMrn(patient?.mrn) || formatPatientMrn(patient?.id)}
          </span>
        </div>
        <div className={styles.patientSearchOptionMeta}>
          {pathwayDisplay ? (
            <span className={styles.patientSearchOptionPathway}>{pathwayDisplay}</span>
          ) : null}
          <span className={styles.patientSearchOptionPhase}>Phase {phase}</span>
          {clinician ? (
            <span className={styles.patientSearchOptionClinician}>{clinician}</span>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div ref={patientSearchRef} className={styles.patientSearchWrap}>
      <div className={styles.patientSearchInput}>
        <Filter className={styles.patientSearchIcon} aria-hidden="true" />
        <input
          id="care-orchestration-task-patient-search"
          type="text"
          className={`${styles.patientSearchNativeInput} ${
            patientSearch ? styles.patientSearchNativeInputSelected : ''
          }`}
          placeholder={searchPlaceholder}
          value={patientSearch ? getPatientSearchLabel(patientSearch) : patientSearchQuery}
          onChange={(event) => {
            if (patientSearch) return;
            setPatientSearchQuery(event.target.value);
          }}
          onFocus={() => {
            if (!patientSearch && patientSearchResults.length) {
              setIsPatientSearchOpen(true);
            }
          }}
          readOnly={!!patientSearch}
        />
        {(patientSearch || patientSearchQuery) && (
          <button
            type="button"
            onClick={clearPatientSearch}
            className={styles.patientSearchClearIcon}
            aria-label="Clear patient search"
          >
            x
          </button>
        )}
      </div>
      {isPatientSearchOpen && !patientSearch ? (
        <div className={styles.patientSearchDropdown}>
          <div className={styles.patientSearchDropdownHeader}>
            {isSearchLoading
              ? 'Searching...'
              : `${patientSearchResults.length} ${
                  patientSearchResults.length === 1 ? 'result' : 'results'
                } found`}
          </div>
          {patientSearchResults.length ? (
            patientSearchResults.map((searchOption) => (
              <button
                key={`${searchOption?.key ?? ''}`}
                type="button"
                className={styles.patientSearchResult}
                onMouseDown={(event) => {
                  event.preventDefault();
                  setPatientSearch(searchOption.value);
                  setPatientSearchQuery('');
                  setPatientSearchResults([]);
                  setIsPatientSearchOpen(false);
                }}
              >
                {renderPatientSearchOption(searchOption)}
              </button>
            ))
          ) : (
            <div className={styles.patientSearchEmpty}>
              No results match &quot;{patientSearchQuery.trim()}&quot;
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

PatientTaskSearch.propTypes = {
  patientSearch: PropTypes.shape({
    kind: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reasonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priority: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredName: PropTypes.string,
    mrn: PropTypes.string,
    phase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pathway: PropTypes.string,
    clinician: PropTypes.string,
    providerName: PropTypes.string,
    doctorName: PropTypes.string,
    cognitiveStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    diabetesStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setPatientSearch: PropTypes.func,
  getPatientSearchLabel: PropTypes.func,
  getPatientSearchPathway: PropTypes.func,
  patientSearchMetadataById: PropTypes.objectOf(
    PropTypes.shape({
      pathway: PropTypes.string,
      phase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      clinician: PropTypes.string,
      patientMrn: PropTypes.string,
    }),
  ),
  searchOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.object,
    }),
  ),
  searchPlaceholder: PropTypes.string,
  isSearchLoading: PropTypes.bool,
  onExpandSearchScope: PropTypes.func,
  onSearchPatients: PropTypes.func,
  onClearSearch: PropTypes.func,
};

export default PatientTaskSearch;
